import { useState } from 'react';
import { Container, Stack, Tabs, Button, Modal, Text, Group, Paper } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useResumeContent, useResumeVersions } from '../api/resumes';
import { useResumeStore } from '../store/resume.store';
import { useArtifacts } from '../api/artifacts';
import ResumeContentLibrary from '../components/resumes/ResumeContentLibrary';
import ResumeContentEditor from '../components/resumes/ResumeContentEditor';
import ResumeCompiler from '../components/resumes/ResumeCompiler';
import ResumeVersionsList from '../components/resumes/ResumeVersionsList';
import ArtifactVersionsList from '../components/artifacts/ArtifactVersionsList';
import { PageHeader } from '../components/common/PageHeader';

export default function ResumePage() {
  const { data: contentItems = [], isLoading: contentLoading } = useResumeContent();
  const { data: versions = [] } = useResumeVersions();
  const { data: artifacts = [] } = useArtifacts(undefined, 'resume'); // Get all resume artifacts
  const { activeContentId, editingType, clearActiveContent } = useResumeStore();
  const isDesktop = useMediaQuery('(min-width: 1408px)');

  const [editorOpen, setEditorOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('library');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreateNew = () => {
    useResumeStore.setState({ editingType: 'create', activeContentId: undefined });
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    clearActiveContent();
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <PageHeader
          title="Resume Management"
          subtitle="Create and manage your resume content library."
          actions={
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleCreateNew}
              variant="filled"
            >
              Add Content
            </Button>
          }
        />

        {/* Tabs */}
        {isDesktop ? (
          <Paper p="md" radius="md" withBorder>
            <Tabs value={activeTab} onChange={setActiveTab}>
              <Tabs.List>
                <Tabs.Tab value="library">Content Library ({contentItems.length})</Tabs.Tab>
                <Tabs.Tab value="compiler">Resume Compiler</Tabs.Tab>
                <Tabs.Tab value="versions">Generated Versions ({versions.length})</Tabs.Tab>
                <Tabs.Tab value="artifacts">Job-Specific Resumes ({artifacts.length})</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="library" pt="lg">
                <ResumeContentLibrary
                  contentItems={contentItems}
                  isLoading={contentLoading}
                  onEdit={(id) => {
                    useResumeStore.setState({ editingType: 'edit', activeContentId: id });
                    setEditorOpen(true);
                  }}
                />
              </Tabs.Panel>

              <Tabs.Panel value="compiler" pt="lg">
                <ResumeCompiler
                  contentItems={contentItems}
                  onResumeCreated={() => setRefreshKey((k) => k + 1)}
                />
              </Tabs.Panel>

              <Tabs.Panel value="versions" pt="lg">
                <ResumeVersionsList versions={versions} contentItems={contentItems} />
              </Tabs.Panel>

              <Tabs.Panel value="artifacts" pt="lg">
                <ArtifactVersionsList
                  key={refreshKey}
                  artifactType="resume"
                  artifacts={artifacts}
                  contentItems={contentItems}
                  onArtifactUpdated={() => setRefreshKey((k) => k + 1)}
                />
              </Tabs.Panel>
            </Tabs>
          </Paper>
        ) : (
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="library">Content Library ({contentItems.length})</Tabs.Tab>
              <Tabs.Tab value="compiler">Resume Compiler</Tabs.Tab>
              <Tabs.Tab value="versions">Generated Versions ({versions.length})</Tabs.Tab>
              <Tabs.Tab value="artifacts">Job-Specific Resumes ({artifacts.length})</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="library" pt="lg">
              <ResumeContentLibrary
                contentItems={contentItems}
                isLoading={contentLoading}
                onEdit={(id) => {
                  useResumeStore.setState({ editingType: 'edit', activeContentId: id });
                  setEditorOpen(true);
                }}
              />
            </Tabs.Panel>

            <Tabs.Panel value="compiler" pt="lg">
              <ResumeCompiler
                contentItems={contentItems}
                onResumeCreated={() => setRefreshKey((k) => k + 1)}
              />
            </Tabs.Panel>

            <Tabs.Panel value="versions" pt="lg">
              <ResumeVersionsList versions={versions} contentItems={contentItems} />
            </Tabs.Panel>

            <Tabs.Panel value="artifacts" pt="lg">
              <ArtifactVersionsList
                key={refreshKey}
                artifactType="resume"
                artifacts={artifacts}
                contentItems={contentItems}
                onArtifactUpdated={() => setRefreshKey((k) => k + 1)}
              />
            </Tabs.Panel>
          </Tabs>
        )}

        {/* Editor Modal */}
        <Modal
          opened={editorOpen}
          onClose={handleCloseEditor}
          title={editingType === 'create' ? 'Create New Content' : 'Edit Content'}
          size="xl"
        >
          <ResumeContentEditor
            contentId={activeContentId}
            editingType={editingType as 'create' | 'edit'}
            onClose={handleCloseEditor}
          />
        </Modal>
      </Stack>
    </Container>
  );
}
